"use client";

import Link from "next/link";
import { personName } from "@/lib/profile/displayName";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import SimpleAuthModal from "@/components/auth/SimpleAuthModal";
import { useAuth } from "@/lib/context/AuthContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import ResponsiveImage from "@/components/ui/ResponsiveImage";
import { ChevronDown, User as UserIcon, LogOut, LayoutDashboard, ClipboardCheck, ClipboardList, Award, FolderOpen, Ticket, LayoutList, AlertTriangle } from "lucide-react";
import NotificationBell from "@/components/notifications/NotificationBell";
import { useDashboardCache } from "@/app/(main)/dashboard/hooks/useDashboardData";
import { Button } from "@/components/ui/button";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // Auth state is server-rendered (the layout seeds AuthProvider with the
  // middleware-validated user), so SSR and the first client render already
  // match – no mounted-gate needed to avoid hydration mismatch.
  const { user, profile, loading, profileLoading, signOut, refreshProfile } = useAuth();
  const isOrganizer = profile?.role === 'organizer' || profile?.role === 'admin' || profile?.role === 'superadmin';
  // Signed in but the profile never landed: the role is UNKNOWN, not "user".
  // Say so and offer a retry instead of silently dropping the organizer entry
  // (2026-09-17: a jammed browser looked exactly like a demoted organizer).
  // A fetch still in flight is not a failure, so the row waits for it rather
  // than flashing "profile did not load" on every sign-in.
  const profileUnavailable = !!user && !profile && !profileLoading;
  const retryProfile = () => { if (user?.id) void refreshProfile(user.id, true); };
  const { t } = useLanguage();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const dashboardCache = useDashboardCache();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const openAuthModal = (mode: 'signin' | 'signup') => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
    setIsMenuOpen(false); // Close mobile menu if open
  };

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
  };

  return (
    <>
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
            <Image src="/logoconfy.png" alt="CONFY Logo" width={28} height={28} className="object-contain flex-shrink-0 sm:w-8 sm:h-8" />
            <span className="font-semibold text-gray-900 text-sm sm:text-base truncate max-w-[100px] sm:max-w-none">{t('header.logo')}</span>
          </Link>

          {/* Desktop Auth/User Menu */}
          <div className="hidden md:flex items-center space-x-3 flex-shrink-0">
            <LanguageSwitcher />
            {user && <NotificationBell />}
            {loading ? (
              /* Unknown auth state must not render as "logged out" –
                 show a neutral skeleton instead of the Login button */
              <div className="flex items-center space-x-2 px-3 py-2" aria-hidden="true">
                <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
                <div className="hidden lg:block h-4 w-24 rounded bg-gray-200 animate-pulse" />
              </div>
            ) : user ? (
              <div className="relative" ref={userMenuRef}>
                <Button
                  variant="ghost"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors h-auto"
                >
                  <ResponsiveImage
                    src={profile?.profile_image_url}
                    alt="Profile"
                    variant="avatar"
                    size="sm"
                  />
                  <span className="text-gray-700 font-semibold hidden lg:block">
                    {personName(profile) || t('header.auth.user')}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </Button>
                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border py-1 z-50">
                    <div className="px-4 py-2 border-b">
                      <p className="text-sm font-semibold text-gray-900">
                        {personName(profile) || t('header.auth.user')}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <Link
                      href="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <UserIcon className="h-4 w-4 mr-2" />
                      {t('header.auth.profile')}
                    </Link>
                    <Link
                      href="/dashboard"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                      onMouseEnter={() => {
                        // Prefetch dashboard data on hover (Udemy-level UX)
                        if (user?.id) {
                          dashboardCache.prefetch(user.id);
                        }
                      }}
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {t('header.auth.dashboard')}
                    </Link>
                    {isOrganizer && (
                      <Link
                        href="/created"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <LayoutList className="h-4 w-4 mr-2" />
                        {t('header.auth.createdByMe')}
                      </Link>
                    )}
                    {profileUnavailable && (
                      <div className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-amber-700 bg-amber-50">
                        <span className="flex items-center min-w-0">
                          <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{t('header.auth.profileUnavailable')}</span>
                        </span>
                        <button
                          type="button"
                          onClick={retryProfile}
                          className="font-semibold underline underline-offset-2 hover:text-amber-900 flex-shrink-0"
                        >
                          {t('header.auth.retryProfile')}
                        </button>
                      </div>
                    )}
                    <div className="border-t my-1" />
                    <Link
                      href="/profile/tickets"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Ticket className="h-4 w-4 mr-2" />
                      {t('header.auth.myTickets')}
                    </Link>
                    <Link
                      href="/profile/tests"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      {t('header.auth.myTests')}
                    </Link>
                    <Link
                      href="/profile/surveys"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <ClipboardList className="h-4 w-4 mr-2" />
                      {t('header.auth.mySurveys')}
                    </Link>
                    <Link
                      href="/profile/certificates"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Award className="h-4 w-4 mr-2" />
                      {t('header.auth.myCertificates')}
                    </Link>
                    <Link
                      href="/profile/materials"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <FolderOpen className="h-4 w-4 mr-2" />
                      {t('header.auth.myMaterials')}
                    </Link>
                    <div className="border-t my-1" />
                    <Button
                      variant="ghost"
                      onClick={handleSignOut}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 h-auto justify-start rounded-none"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('header.auth.signOut')}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={() => openAuthModal('signin')}
              >
                {t('header.auth.signIn')}
              </Button>
            )}
          </div>

          {/* Mobile Menu: Language + Notifications + Menu Button */}
          <div className="md:hidden flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <LanguageSwitcher />
            {user && <NotificationBell />}
            <Button
              variant="ghost"
              size="sm"
              className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors min-w-[36px] min-h-[36px] sm:min-w-[40px] sm:min-h-[40px] flex items-center justify-center"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 bg-white">
            <nav className="flex flex-col space-y-2">
              <div className="flex flex-col space-y-2 pt-2">
                {user ? (
                  <>
                    <div className="flex items-center space-x-3 px-2 py-2">
                      <ResponsiveImage
                        src={profile?.profile_image_url}
                        alt="Profile"
                        variant="avatar"
                        size="sm"
                      />
                      <span className="text-gray-700 font-semibold">
                        {personName(profile) || t('header.auth.user')}
                      </span>
                    </div>
                    <Link
                      href="/profile"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('header.auth.profile')}
                    </Link>
                    <Link
                      href="/dashboard"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                      onTouchStart={() => {
                        // Prefetch dashboard data on touch (mobile UX)
                        if (user?.id) {
                          dashboardCache.prefetch(user.id);
                        }
                      }}
                    >
                      {t('header.auth.dashboard')}
                    </Link>
                    {isOrganizer && (
                      <Link
                        href="/created"
                        className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {t('header.auth.createdByMe')}
                      </Link>
                    )}
                    {profileUnavailable && (
                      <div className="flex items-center justify-between gap-2 px-2 py-1 text-sm text-amber-700">
                        <span className="flex items-center min-w-0">
                          <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{t('header.auth.profileUnavailable')}</span>
                        </span>
                        <button
                          type="button"
                          onClick={retryProfile}
                          className="font-semibold underline underline-offset-2 hover:text-amber-900 flex-shrink-0"
                        >
                          {t('header.auth.retryProfile')}
                        </button>
                      </div>
                    )}
                    <div className="border-t border-gray-200 my-2" />
                    <Link
                      href="/profile/tickets"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('header.auth.myTickets')}
                    </Link>
                    <Link
                      href="/profile/tests"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('header.auth.myTests')}
                    </Link>
                    <Link
                      href="/profile/surveys"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('header.auth.mySurveys')}
                    </Link>
                    <Link
                      href="/profile/certificates"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('header.auth.myCertificates')}
                    </Link>
                    <Link
                      href="/profile/materials"
                      className="text-gray-700 hover:text-blue-600 transition-colors font-semibold text-left px-2 py-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('header.auth.myMaterials')}
                    </Link>
                    <div className="border-t border-gray-200 my-2" />
                    <Button
                      variant="ghost"
                      onClick={handleSignOut}
                      className="text-gray-700 hover:text-red-600 transition-colors font-semibold text-left px-2 py-1 h-auto justify-start"
                    >
                      {t('header.auth.signOut')}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => openAuthModal('signin')}
                    className="mx-2"
                  >
                    {t('header.auth.signIn')}
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>

    {/* Auth Modal - moved outside header */}
    {isAuthModalOpen && (
      <SimpleAuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode={authMode}
      />
    )}
    </>
  );
}